/**
 * Diff the AI-extracted draft against what the PM actually submitted, to
 * capture {extracted → corrected} eval pairs (EX.9 / D.6). Pure + shared by the
 * PDF and chat intake pages. `intake_notes` is excluded — it's auto-composed
 * and always edited, so it would be pure noise in the eval set.
 */
import type {
  ExtractionCorrectionItem,
  ProjectCreateInput,
} from '@vari-crew/schemas';

const TRACKED_SCALARS: Array<keyof ProjectCreateInput> = [
  'title',
  'client_name',
  'kind',
  'priority',
  'sap_order_number',
  'sap_project_id',
  'customer_number',
  'design_job_number',
  'design_pd_number',
  'site_contact_name',
  'site_contact_phone',
  // origin_warehouse is a list (VCS-203), not a scalar — excluded from the
  // scalar correction diff.
  'interior_designer',
  'csm',
  'sales_executive',
  'delivery_install_budget_cents',
];

const ADDRESS_SUBFIELDS = ['line1', 'line2', 'city', 'state', 'postal_code'] as const;

/** Normalize a draft value to the comparable string we store (null = empty). */
function norm(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v === 'string') {
    const t = v.trim();
    return t === '' ? null : t;
  }
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export function diffDraftCorrections(
  original: Partial<ProjectCreateInput> | null | undefined,
  final: Partial<ProjectCreateInput> | null | undefined,
): ExtractionCorrectionItem[] {
  if (!original || !final) return [];
  const out: ExtractionCorrectionItem[] = [];
  for (const f of TRACKED_SCALARS) {
    const a = norm(original[f]);
    const b = norm(final[f]);
    if (a !== b) out.push({ field: f, extracted_value: a, corrected_value: b });
  }
  const oa = original.site_address ?? null;
  const fa = final.site_address ?? null;
  for (const sub of ADDRESS_SUBFIELDS) {
    const a = norm(oa?.[sub]);
    const b = norm(fa?.[sub]);
    if (a !== b) {
      out.push({
        field: `site_address.${sub}`,
        extracted_value: a,
        corrected_value: b,
      });
    }
  }
  return out;
}
